import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { environment } from '@env';
import { PageStatsDeviceAndOs } from '@models/pages/page';
import { LoaderService } from '@services/loader.service';
import { PagesService } from '@services/pages.service';
import { ChartData, ChartOptions, ChartType } from 'chart.js';
import moment from 'moment';
// import { Color, Label, MultiDataSet } from 'ng2-charts';
import { finalize, map } from 'rxjs/operators';

@Component({
  selector: 'wsb-landing-devices',
  templateUrl: './landing-devices.component.html',
  styleUrls: ['./landing-devices.component.scss'],
  standalone: false
})
export class LandingDevicesComponent implements OnInit {
  @Input() periods: any;
  @Input() loadingPeriod: any;
  @Input() page: any;

  public readonly today = new Date();

  dateFrom: UntypedFormControl = new UntypedFormControl();
  dateTo: UntypedFormControl = new UntypedFormControl();
  period: UntypedFormControl = new UntypedFormControl();

  public doughnutChartLabels: string[] = ['Download Sales', 'In-Store Sales', 'Mail-Order Sales'];
  public doughnutChartData = [
    {
      data: [350],
      label: '',
      offset: '10px',
      spacing: '10px'
    }
  ];
  public doughnutChartType: ChartType = 'doughnut';

  public doughnutLegend = false;
  public doughnutType: ChartType = 'doughnut';
  public doughnutLabels: string[] = [];
  public doughnutOptions?: ChartOptions<'doughnut'> = {
    responsive: true,
    cutout: 90
  };
  public doughnutDesktopData: ChartData<'doughnut'> = {
    datasets: [
      {
        data: [0, 1],
        label: '',
        backgroundColor: [environment.chartsColorPalette[0], '#f3f3f3'],
        borderColor: environment.chartsColorPalette[2],
        hoverBorderColor: environment.chartsColorPalette[3]
      }
    ]
  };
  public doughnutTabletData: ChartData<'doughnut'> = {
    datasets: [
      {
        data: [0, 1],
        label: '',
        backgroundColor: [environment.chartsColorPalette[0], '#f3f3f3'],
        borderColor: environment.chartsColorPalette[2],
        hoverBorderColor: environment.chartsColorPalette[3]
      }
    ]
  };
  public doughnutSmartphoneData: ChartData<'doughnut'> = {
    datasets: [
      {
        data: [0, 1],
        label: '',
        backgroundColor: [environment.chartsColorPalette[0], '#f3f3f3'],
        borderColor: environment.chartsColorPalette[2],
        hoverBorderColor: environment.chartsColorPalette[3]
      }
    ]
  };
  public doughnutOthersData: ChartData<'doughnut'> = {
    datasets: [
      {
        data: [0, 1],
        label: '',
        backgroundColor: [environment.chartsColorPalette[0], '#f3f3f3'],
        borderColor: environment.chartsColorPalette[2],
        hoverBorderColor: environment.chartsColorPalette[3]
      }
    ]
  };

  percDesktop;
  percSmartphone;
  percTablet;
  percOther;

  totalDesktop;
  totalSmartphone;
  totalTablet;
  totalOther;
  total;

  landingDeviceStats;

  totalDevice;
  totalOs;

  constructor(private pagesService: PagesService, private loaderService: LoaderService) {}

  ngOnInit(): void {
    this.onPageChange();
    this.onSelectionChange(this.loadingPeriod);
  }

  onPageChange() {
    this.pagesService.selectionChange.subscribe(page => {
      this.getPageById(page?.idPage);
      this.getDeviceStats(page?.idPage, this.dateFrom, this.dateTo);
    });
  }

  getPageById(idPage) {
    if (!idPage) {
      return;
    }
    this.pagesService.getPageById(idPage).subscribe(page => {
      this.page = page;
    });
  }

  onSelectionChange(period) {
    this.dateFrom.setValue(this.setDateForFc(period.dateFrom));
    this.dateTo.setValue(this.setDateForFc(period.dateTo));
    this.getDeviceStats(this.page?.idPage, period.dateFrom, period.dateTo);
  }

  search() {
    this.getDeviceStats(this.page?.idPage, this.dateFrom.value, this.dateTo.value);
  }

  getDeviceStats(idPage, dateFrom, dateTo) {
    if (!idPage) {
      return;
    }

    this.loaderService.show();
    this.pagesService
      .getPagesStatsGetDeviceAndOs(idPage, this.fixDate(dateFrom), this.fixDate(dateTo))
      .pipe(
        finalize(() => this.loaderService.hide()),
        map(data => {
          const devices = data.device.filter(device => device.statistics.total !== 0);
          const oss = data['operating-system'].filter(os => os.statistics.total !== 0);

          const newData: PageStatsDeviceAndOs = {
            device: devices,
            'operating-system': oss
          } as PageStatsDeviceAndOs;

          return newData;
        })
      )
      .subscribe(stats => {
        this.landingDeviceStats = stats;

        this.totalDevice = stats.device.reduce((total, device) => total + device.statistics.total, 0);
        this.totalOs = stats['operating-system'].reduce((total, os) => total + os.statistics.total, 0);

        for (const device of stats.device) {
          switch (device.resource) {
            case 'Desktop':
              this.percDesktop = (device.statistics.total * 100) / this.totalDevice;
              this.percDesktop = (Math.round(this.percDesktop * 100) / 100).toFixed(2);

              this.totalDesktop = device.statistics.total;
              this.doughnutDesktopData.datasets[0].data = [device.statistics.total, this.totalDevice - device.statistics.total];
              break;
            case 'Smartphone':
              this.percSmartphone = (device.statistics.total * 100) / this.totalDevice;
              this.percSmartphone = (Math.round(this.percSmartphone * 100) / 100).toFixed(2);
              this.totalSmartphone = device.statistics.total;
              this.doughnutSmartphoneData.datasets[0].data = [device.statistics.total, this.totalDevice - device.statistics.total];
              break;
            case 'Tablet':
              this.percTablet = (device.statistics.total * 100) / this.totalDevice;
              this.percTablet = (Math.round(this.percTablet * 100) / 100).toFixed(2);
              this.totalTablet = device.statistics.total;
              this.doughnutTabletData.datasets[0].data = [device.statistics.total, this.totalDevice - device.statistics.total];
              break;
            case 'Unknown':
              this.percOther = (device.statistics.total * 100) / this.totalDevice;
              this.percOther = (Math.round(this.percOther * 100) / 100).toFixed(2);
              this.totalOther = device.statistics.total;
              this.doughnutOthersData.datasets[0].data = [device.statistics.total, this.totalDevice - device.statistics.total];
              break;
            default:
              break;
          }
        }
      });
  }

  setDateForFc(date) {
    return new Date(
      moment(date)
        .format('ddd MMM DD YYYY HH:mm:ss')
        .toString()
    );
  }

  osPerc(total) {
    const perc = (total * 100) / this.totalOs;
    return (Math.round(perc * 100) / 100).toFixed(2);
  }

  fixDate(date) {
    return (
      moment(date)
        .format('YYYY-MM-DDTHH:mm:ss')
        .toString() + 'Z'
    );
  }
}
